import {
    Box,
    Icon,
    IconButton,
    styled,
    Tooltip,
    useTheme,
} from "@mui/material";

import { useState, useEffect } from "react";
import { Paragraph } from 'app/components/Typography';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';

import Swal from 'sweetalert2'
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';

import { GetStories, DeleteStory, TogglePinned, ToggleMain } from "../../../store/actions/storyAction"
import { STORY_SUCCESS_MESSAGE_CLEAR, STORY_ERROR_CLEAR } from "../../../store/types/storyTypes"

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& .ant-empty-img-1': {
        fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
    },
}));

function CustomNoRowsOverlay() {
    return (
        <StyledGridOverlay>
            <Box sx={{ mt: 1 }}>No Stories</Box>
        </StyledGridOverlay>
    );
}

const StoriesTable = () => {
    const { palette } = useTheme();
    const bgError = palette.error.main;
    const bgPrimary = palette.primary.main;

    const [callbackResponse, setCallbackResponse] = useState({ type: "", message: "" });

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        stories,
        errorMessage,
        successMessage
    } = useSelector((state) => state.Stories);

    useEffect(() => {
        dispatch(GetStories()).then(() => {
            dispatch({ type: STORY_SUCCESS_MESSAGE_CLEAR })
        })
    }, []);

    const handleDelete = (storyId) => {
        Swal.fire({
            title: "Are you sure?",
            showCancelButton: true,
            text: "This action cannot be undone!",
            confirmButtonText: 'Yes',
            cancelButtonText: `No`,
            cancelButtonColor: "#EE7364"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(DeleteStory(storyId));
            }
        });
    };

    const handleTogglePinned = (storyId) => {
        dispatch(TogglePinned(storyId));
    };

    const handleToggleMain = (storyId) => {
        dispatch(ToggleMain(storyId));
    };

    useEffect(() => {
        if (successMessage.delete || successMessage.update || successMessage.create) {
            const msg = successMessage.delete || successMessage.update || successMessage.create;
            setCallbackResponse({ type: "success", message: msg });
            setTimeout(() => {
                dispatch({ type: STORY_SUCCESS_MESSAGE_CLEAR })
            }, 2000);
        }

        if (errorMessage) {
            setCallbackResponse({ type: "error", message: errorMessage });
            dispatch({ type: STORY_ERROR_CLEAR })
        }
    }, [successMessage, errorMessage])

    const columns = [
        {
            field: 'id', headerName: 'ID', width: 50,
            renderCell: (params) => (
                <strong>{params.row.id}</strong>
            ),
        },
        { field: 'title', headerName: 'Title', width: 200 },
        { field: 'description', headerName: 'Description', width: 250 },
        { field: 'status', headerName: 'Status', width: 100 },
        {
            field: 'date', headerName: 'Date', width: 150,
            renderCell: (params) => {
                const formattedDate = new Date(params.row.date).toISOString().split('T')[0];
                return <div>{formattedDate}</div>;
            },
        },
        {
            field: 'pinned', headerName: 'Pinned', width: 80,
            renderCell: (params) => (
                <Tooltip title={params.row.pinned ? "Unpin" : "Pin"}>
                    <IconButton
                        size="small"
                        onClick={() => handleTogglePinned(params.row._id)}
                        sx={{ color: params.row.pinned ? bgPrimary : 'inherit' }}
                    >
                        <Icon fontSize="small">push_pin</Icon>
                    </IconButton>
                </Tooltip>
            ),
        },
        {
            field: 'isMain', headerName: 'Main', width: 80,
            renderCell: (params) => (
                <Tooltip title={params.row.isMain ? "Unset Main" : "Set Main"}>
                    <IconButton
                        size="small"
                        onClick={() => handleToggleMain(params.row._id)}
                        sx={{ color: params.row.isMain ? bgPrimary : 'inherit' }}
                    >
                        <Icon fontSize="small">star</Icon>
                    </IconButton>
                </Tooltip>
            ),
        },
        {
            field: 'action', headerName: '', width: 100, sortable: false, disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <>
                        <IconButton onClick={() => navigate(`/edit-story/${params.row._id}`)}>
                            <Icon fontSize="small">edit</Icon>
                        </IconButton>
                        <IconButton onClick={() => handleDelete(params.row._id)}>
                            <Icon fontSize="small" color="error">delete</Icon>
                        </IconButton>
                    </>
                )
            },
        },
    ];

    let rows = [];
    if (stories && stories.length > 0) {
        rows = stories;
    }

    let idCounter = 1;
    const generateIncrementalId = (() => {
        return idCounter++;
    });

    const getRowId = (row) => row._id;

    const dataWithIds = rows.map((row) => ({ ...row, id: generateIncrementalId() }));

    return (
        <Box width="100%" overflow="auto">
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={!!callbackResponse.message}
                autoHideDuration={6000}
                onClose={() => setCallbackResponse({ type: "", message: "" })}
            >
                <Alert
                    severity={callbackResponse.type}
                    variant="filled"
                    sx={{ width: '300px' }}
                >
                    {callbackResponse.message}
                </Alert>
            </Snackbar>

            <DataGrid
                autoHeight
                columns={columns}
                rows={dataWithIds}
                getRowId={getRowId}
                slots={{
                    toolbar: GridToolbar,
                    noRowsOverlay: CustomNoRowsOverlay,
                }}
                sx={{ '--DataGrid-overlayHeight': '300px' }}
            />
        </Box>
    );
};

export default StoriesTable;

